import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtDate, fmtDateTime } from "@/lib/dates";

const ENTITY_TYPES = ["client", "project", "task", "leave"];

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [entity, setEntity] = useState("all");

  useEffect(() => {
    (async () => {
      const params = {};
      if (entity !== "all") params.entity_type = entity;
      const r = await api.get("/audit-logs", { params });
      setItems(r.data || []);
    })();
  }, [entity]);

  const actionColor = {
    create: "status-completed",
    update: "status-ongoing",
    delete: "status-rejected",
    decide: "status-completed",
  };

  return (
    <div className="min-h-screen bg-white" data-testid="audit-log-page">
      <PageHeader title="Activity Log" subtitle="System Audit" />

      <div className="px-4 sm:px-8 py-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="gc-label">Filter</div>
          <Select value={entity} onValueChange={setEntity}>
            <SelectTrigger className="w-48 rounded-sm" data-testid="filter-entity">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entity types</SelectItem>
              {ENTITY_TYPES.map((e) => (<SelectItem key={e} value={e}>{e}</SelectItem>))}
            </SelectContent>
          </Select>
          <div className="ml-auto text-xs text-slate-500 font-mono-data">{items.length} entries</div>
        </div>

        <div className="border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Time</th>
                <th className="text-left px-4 py-2.5">Actor</th>
                <th className="text-left px-4 py-2.5">Action</th>
                <th className="text-left px-4 py-2.5">Entity</th>
                <th className="text-left px-4 py-2.5">ID</th>
                <th className="text-left px-4 py-2.5">Details</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-xs text-slate-400">No activity</td></tr>
              )}
              {items.map((it) => (
                <tr key={it.id} className="border-b border-slate-100" data-testid={`audit-row-${it.id}`}>
                  <td className="px-4 py-2.5 font-mono-data text-xs text-slate-500 whitespace-nowrap">
                    {fmtDateTime(it.created_at)}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="text-xs font-medium">{it.actor_name || "—"}</div>
                    <div className="text-[10px] text-slate-400 font-mono-data">{it.actor_email}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`status-pill ${actionColor[it.action] || "status-pending"}`}>{it.action}</span>
                  </td>
                  <td className="px-4 py-2.5 text-xs">{it.entity_type}</td>
                  <td className="px-4 py-2.5 font-mono-data text-[11px] text-slate-500">{it.entity_id || "—"}</td>
                  <td className="px-4 py-2.5 text-[11px] text-slate-600 font-mono-data">
                    {it.meta && Object.keys(it.meta).length > 0 ? JSON.stringify(it.meta) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
